import React, { useState } from 'react';
import { X } from 'lucide-react';

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    companyWallet: string;
    companyName: string;
    companyEmail: string;
    companyAddress: string;
    companyPhone: string;
    initialAmount: string;
  }) => void;
  isLoading?: boolean;
}

const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    companyWallet: '',
    companyName: '',
    companyEmail: '',
    companyAddress: '',
    companyPhone: '',
    initialAmount: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      companyWallet: '',
      companyName: '',
      companyEmail: '',
      companyAddress: '',
      companyPhone: '',
      initialAmount: '',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-96 shadow-lg rounded-md bg-[#182233]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-white">Create Company</h3>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-300"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Company Wallet Address</label>
            <input
              type="text"
              value={formData.companyWallet}
              onChange={(e) => setFormData({ ...formData, companyWallet: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter wallet address"
              required
              disabled={isLoading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Company Name</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter company name"
              required
              disabled={isLoading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
            <input
              type="email"
              value={formData.companyEmail}
              onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter email"
              required
              disabled={isLoading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Address</label>
            <input
              type="text"
              value={formData.companyAddress}
              onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter address"
              required
              disabled={isLoading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Phone</label>
            <input
              type="text"
              value={formData.companyPhone}
              onChange={(e) => setFormData({ ...formData, companyPhone: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter phone"
              required
              disabled={isLoading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Initial Amount (USD)</label>
            <input
              type="number"
              value={formData.initialAmount}
              onChange={(e) => setFormData({ ...formData, initialAmount: e.target.value })}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter initial amount"
              min="0"
              step="0.01"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2] disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompanyModal;
